/**
 * Agrupa produtos/planos/cursos ligados por equivalentIds (transitivo) e escolhe
 * UM representante da vitrine por grupo — capa/categoria/ordem ficam só nele.
 */

export function buildCatalogEquivalenceGroups(catalogProducts = []) {
  const byId = new Map(catalogProducts.map((product) => [product.id, product]));
  const parent = new Map();

  const find = (id) => {
    if (!parent.has(id)) parent.set(id, id);
    if (parent.get(id) !== id) parent.set(id, find(parent.get(id)));
    return parent.get(id);
  };

  const union = (a, b) => {
    const rootA = find(a);
    const rootB = find(b);
    if (rootA !== rootB) parent.set(rootA, rootB);
  };

  catalogProducts.forEach((product) => {
    (product.equivalentIds || []).forEach((eq) => {
      if (byId.has(eq.id)) union(product.id, eq.id);
    });
  });

  const groups = new Map();
  catalogProducts.forEach((product) => {
    const root = find(product.id);
    if (!groups.has(root)) groups.set(root, []);
    groups.get(root).push(product);
  });

  const getGroupMembers = (productId) => groups.get(find(productId)) || [];

  return { byId, groups, getGroupMembers };
}

/** Um card na Home por grupo: capa + categoria + showInCatalog, desempate por ordem. */
export function pickCatalogVitrinePrimary(members = []) {
  if (!members.length) return null;

  const pool = members.filter(
    (product) =>
      product.catalogImage?.url &&
      product.showInCatalog !== false &&
      product.productCategoryId
  );

  if (pool.length === 0) return null;
  if (pool.length === 1) return pool[0];

  return [...pool].sort((a, b) => {
    const orderDiff = (a.order ?? 0) - (b.order ?? 0);
    if (orderDiff !== 0) return orderDiff;

    return (a.name || "").localeCompare(b.name || "", "pt-BR");
  })[0];
}

export function getCatalogVitrinePrimary(product, catalogProducts = []) {
  if (!product) return null;
  const { getGroupMembers } = buildCatalogEquivalenceGroups(catalogProducts);
  return pickCatalogVitrinePrimary(getGroupMembers(product.id));
}

export function isCatalogVitrineRepresentative(product, catalogProducts = []) {
  const primary = getCatalogVitrinePrimary(product, catalogProducts);
  return !!primary && primary.id === product.id;
}

export function filterCatalogVitrineRepresentatives(products, catalogProducts = []) {
  return products.filter((product) => isCatalogVitrineRepresentative(product, catalogProducts));
}
